import { CustomerPoliciesState, SessionInfo } from '@ca/common'
import {
  Customer,
  CustomerLoginResponse,
  Deal,
  DealType,
  LoginResponse,
  StaffLoginResponse,
  StoreLoginResponse,
  StrapiID,
  TokenResponse,
} from '@ca/common/types'
import useBundleType from '@order/hooks/useBundleType'
import SessionUtils from '@order/utils/SessionUtils'
import * as Sentry from '@sentry/react-native'
import { trpc } from 'api/trpc'
import AppConfig from 'config'
import { nanoid } from 'nanoid/non-secure'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import stateStorage from 'storage/stateStorage'
import { useThrottledCallback } from 'use-debounce'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { devtools } from 'utils/devtools'
import useAppState from './useAppState'
import { ResolvedDealsContext } from '@order/hooks/state/orderState'

/**
 * Extra session context ie promoId selected before login
 */
export type SessionDealContext = {
  dealName: string
  dealCode: string
  dealId: StrapiID
  dealType: DealType
  basePackageId: StrapiID
  activePackageId?: StrapiID
  filter?: Deal['filter']
  frames: number
}

export type DealsContext = {
  base?: SessionDealContext
  upgrade?: SessionDealContext
  extra?: SessionDealContext
  extraUpgrade?: SessionDealContext
}

export type DealContextKey = keyof DealsContext

export const DealCtxKeys = {
  base: {
    base: 'base',
    upgrade: 'upgrade',
  },
  extra: {
    base: 'extra',
    upgrade: 'extraUpgrade',
  },
} as const

export type PendingOrder = {
  savedOrderId: string
  deal: SessionDealContext
  type?: 'sv' | 'vf' | 'office'
  uiMode?: 'default' | 'select'
}

type SessionState = {
  sessionId: string | null
  customer: CustomerLoginResponse | null
  deals: DealsContext
  info: SessionInfo
  staff: StaffLoginResponse | null
  store: StoreLoginResponse | null
  pendingOrder: PendingOrder | null
  storeLogin: string
  staffRevalidate: boolean
}

export const getSessionDealContext = (
  deal: Deal,
  packageDetails: {
    activePackageId?: StrapiID
    basePackageId?: StrapiID
  }
) => {
  return {
    dealName: deal.title,
    dealCode: deal.code,
    dealId: deal.id,
    frames: deal.frames,
    dealType: deal.type || 'normal',
    filter: deal.filter,
    basePackageId: packageDetails.basePackageId || deal.packages[0],
    activePackageId: packageDetails.activePackageId,
  } satisfies SessionDealContext
}

type SessionActions = {
  setCustomer: (user: CustomerLoginResponse | undefined) => void
  setCustomerData: (customer: Partial<Customer>) => void
  setStaff: (staff: StaffLoginResponse) => void
  setStore: (store: StoreLoginResponse | null) => void
  setStoreLogin: (storeLogin: string) => void
  setDeal: (store: SessionDealContext | undefined, type: DealContextKey) => void
  startNewSession: () => void
  endSession: () => void
  resetDeals: (clearPendingOrder?: boolean) => void
  setPendingOrder: (pendingOrder: PendingOrder | null) => void
  setInfo: (info: Partial<SessionInfo>) => void
  setCustomerPolicies: (policies: CustomerPoliciesState) => void
  updateTokens: (
    tokens: TokenResponse,
    type: 'customer' | 'staff' | 'store'
  ) => void
  invalidateStaff: () => void
}

export const useSession = create<
  SessionState & SessionActions,
  [
    ['zustand/devtools', { name: 'session' }],
    [
      'zustand/persist',
      { store: StoreLoginResponse | null; storeLogin: string }
    ]
  ]
>(
  devtools(
    persist(
      (set, get) => ({
        sessionId: null,
        customer: null,
        staff: null,
        store: null,
        storeLogin: '',
        pendingOrder: null,
        staffRevalidate: false,
        info: {},
        deals: {},
        setStoreLogin: (storeLogin) => {
          set({ storeLogin })
        },
        invalidateStaff: () => {
          set({ staffRevalidate: true })
        },
        resetDeals: (clearPendingOrder = true) =>
          set((v) => ({
            ...v,
            deals: {},
            pendingOrder: clearPendingOrder ? null : v.pendingOrder,
          })),
        setDeal: (deal, key = 'base') => {
          return set((v) => ({
            ...v,
            deals: {
              ...v.deals,
              [key]: deal,
            },
          }))
        },
        setInfo: (info) => set((v) => ({ ...v, info: { ...v.info, ...info } })),
        setCustomerPolicies: (policies) =>
          set((v) => {
            if (!v.customer) {
              return v
            }
            return {
              ...v,
              customer: {
                ...v.customer,
                policies,
              },
            }
          }),

        /**
         * Sets a new customer and new session id
         */
        setCustomer: (customer) =>
          set({ customer, sessionId: nanoid(), info: {} }),
        setCustomerData: (data) => {
          set((v) => {
            if (!v.customer) {
              return v
            }
            return {
              ...v,
              customer: {
                ...v.customer,
                data: {
                  ...v.customer.data,
                  ...data,
                },
              },
            }
          })
        },
        setStaff: ({ store, ...staff }) =>
          set((s) => ({
            staff,
            staffRevalidate: false,
            // Update store
            store: s.store
              ? {
                  ...s.store,
                  data: store || s.store.data,
                }
              : undefined,
          })),
        setStore: (store) => set({ store }),
        setPendingOrder: (pendingOrder) => set({ pendingOrder }),
        startNewSession: () => set({ sessionId: nanoid() }),
        endSession: () =>
          set({
            sessionId: null,
            customer: null,
            staff: null,
            pendingOrder: null,
            deals: {},
            info: {},
          }),

        updateTokens: (
          tokens: TokenResponse,
          type: 'customer' | 'staff' | 'store'
        ) => {
          const entity = get()[type]
          if (!entity) {
            return
          }
          set({
            [type]: { ...entity, ...tokens },
          })
        },
      }),
      {
        name: 'session',
        partialize: (state) => ({
          store: state.store,
          staff: AppConfig.isDev ? state.staff : undefined,
          storeLogin: state.storeLogin,
          customer: AppConfig.isDev ? state.customer : undefined,
        }),
        storage: createJSONStorage(() => stateStorage),
      }
    ),
    {
      name: 'session',
    }
  )
)

export const useTimestampSync = () => {
  const state = useAppState()
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const { refetch } = trpc.health.useQuery(
    {},
    {
      placeholderData: (v) => v,
      staleTime: Infinity,
      // Refresh the server timestamp every 15 minutes
    }
  )

  // Refresh the server timestamp every 15 minutes, and on app state active
  useEffect(() => {
    if (state !== 'active') {
      return
    }

    const refresh = () => {
      refetch()
      timeout.current = setTimeout(refresh, 1000 * 60 * 15)
    }
    refresh()
    return () => timeout.current && clearTimeout(timeout.current)
  }, [state, refetch])
}

const useTimestamp = () => {
  const q = trpc.health.useQuery(
    {},
    {
      placeholderData: (v) => v,
      staleTime: Infinity,
    }
  )

  const getTimestamp = useCallback(() => {
    if (!q.data) {
      return {
        ms: Date.now(),
        type: 'client',
      }
    }
    if (q.data.timestampMs) {
      const diff = Date.now() - q.dataUpdatedAt
      const serverTime = q.data.timestampMs + diff
      return {
        ms: serverTime,
        type: 'server',
      }
    }

    return {
      ms: Date.now(),
      type: 'client',
    }
  }, [q.data, q.dataUpdatedAt])

  return getTimestamp
}

const logged: Record<string, boolean> = {}

const getLogKey = (type: string, id: string, exp: string) => {
  return `${type}:${id}:${exp}`
}

const useValidEntity = <T extends { id: StrapiID }>(
  entity: LoginResponse<T> | null | undefined,
  type: string
) => {
  const getTimestamp = useTimestamp()

  const logWarning = useThrottledCallback(
    (data: {
      error: any
      entity: LoginResponse<T> | null | undefined
      type: string
      timestamp: { ms: number; type: string }
    }) => {
      const key = getLogKey(
        data.type,
        data.entity?.data?.id?.toString() ?? 'unknown',
        data.entity?.exp?.toString() ?? '-'
      )
      if (logged[key]) {
        return
      }
      logged[key] = true
      Sentry.captureException(data.error, {
        tags: {
          entityType: data.type,
          entity: data.entity?.data?.id,
          location: 'useValidEntity',
          timestampType: data.timestamp.type,
          timestampMs: data.timestamp.ms,
        },
        extra: {
          entity: data.entity?.data,
          exp: data.entity?.exp,
          refresh_exp: data.entity?.refresh_exp,
        },
        level: 'warning',
      })
      console.warn(data.error)
    },
    5000
  )

  return useMemo(() => {
    // No need to check anything and log any warnings
    if (!entity) {
      return undefined
    }
    const timestamp = getTimestamp()
    const valid = SessionUtils.isEntityValid(entity, timestamp.ms)
    if (!valid) {
      const error = new Error(`Invalid ${type}`)
      logWarning({
        error,
        entity,
        type,
        timestamp,
      })
      return undefined
    }
    return entity?.data
  }, [entity, getTimestamp])
}

export const useCustomer = () => {
  const customer = useSession((state) => state.customer)
  return useValidEntity(customer, 'customer')
}

export const useStaff = () => {
  const staff = useSession((state) => state.staff)
  return useValidEntity(staff, 'staff')
}

export const useStore = () => {
  const store = useSession((state) => state.store)
  return useValidEntity(store, 'store')
}

export const useSessionDeal = () => {
  const { dealKey, dealUpgradeKey } = useBundleType()
  const upgrade = useSession((s) => s.deals[dealUpgradeKey])
  const deal = useSession((s) => s.deals[dealKey])
  return upgrade || deal
}

export const resolveDealsContext = (
  deals: DealsContext
): ResolvedDealsContext => ({
  base: deals.upgrade ?? deals.base,
  extra: deals.extraUpgrade ?? deals.extra,
})

export const useResolvedDealsContext = () => {
  const deals = useSession((s) => s.deals)
  return resolveDealsContext(deals)
}

export default useSession
